import { useState } from 'react';
import { Trans } from 'services/i18n';
import { ErrorBanner, WarningBanner } from 'ui/banner';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Help24 } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { StyledRouterLink, Link } from 'ui/links';
import { Modal, ModalFooter, ModalHeader, MODAL_VARIANTS } from 'ui/modal';
import { Popover, PopoverPlacements } from 'ui/popover';
import { Skeleton } from 'ui/skeleton';
import { Subtitle1 } from 'ui/theme';
import { localizeNumber } from 'utils/l10n';

import {
  StyledBodySection,
  StyledCheckbox,
  StyledDescList,
  StyledFlexSection,
  StyledInputLabel,
  StyledInputWrapper,
  StyledList,
  StyledModalBodyPlusHalf,
  StyledPopoverContent,
  StyledPopoverTrigger,
} from './Styled';
import { useListDetailsContext } from '../../contexts/listDetails';
import { useModalsContext } from '../../contexts/modals';
import {
  useDeleteUsersInListMutation,
  useListId,
  useListSizeByIdQuery,
  useTranslation,
} from '../../hooks';
import { DeleteUsersInListParams } from '../../types';
import { PAGE_VARIATIONS } from 'modules/segmentation/constants';

export const testIds = {
  bannerError: 'segmentation-duil-banner-error',
  bannerWarning: 'segmentation-duil-banner-warning',
  buttonSchedule: 'segmentation-duil-btn-schedule',
  chkboxConfirm: 'segmentation-duil-chkbox-confirm',
  inputConfirm: 'segmentation-duil-input-confirm',
  modal: 'segmentation-duil',
};

export const DeleteUsersInListModal = () => {
  const { t } = useTranslation();
  const listId = useListId();
  const { list, pageVariation } = useListDetailsContext();
  const { size: listSize = 0, isLoading: isListSizeLoading } = useListSizeByIdQuery(
    listId,
    pageVariation === PAGE_VARIATIONS.STATIC,
  );
  const { hideModal: closeModal } = useModalsContext();
  const { deleteUsers } = useDeleteUsersInListMutation();
  const [checkboxConfirm, setCheckboxConfirm] = useState<boolean>(false);
  const [inputConfirm, setInputConfirm] = useState<string>('');
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
  const deleteString = t('modal.deleteUsers.confirmationString');
  const canSubmit = checkboxConfirm && inputConfirm === deleteString;

  const scheduleDeletion = () => {
    const params: DeleteUsersInListParams = { emailListId: list.id };
    deleteUsers(params);
  };

  const isListEmpty = listSize === 0 && !isListSizeLoading;

  const deleteUsersModalBody = isListEmpty ? (
    <ErrorBanner data-test={testIds.bannerError} title={t('modal.deleteUsers.errorBanner.title')}>
      {t('modal.deleteUsers.errorBanner.content')}
    </ErrorBanner>
  ) : (
    <>
      <section>
        <Subtitle1>{t('modal.deleteUsers.description.title')}</Subtitle1>
        <StyledList>
          <li>
            <Trans
              t={t}
              i18nKey="modal.deleteUsers.description.bullet1"
              components={{
                strong: <strong />,
              }}
            >
              Each user&apos;s <strong>profile data, subscription, settings, custom events,</strong>{' '}
              and <strong>system events</strong> (ex. sends, opens, clicks, etc.) will be
              permanently erased
            </Trans>
          </li>
          <li>
            <Trans
              t={t}
              i18nKey="modal.deleteUsers.description.bullet2"
              components={{
                strong: <strong />,
              }}
            >
              System events will be removed from historic <strong>campaign metrics</strong>
            </Trans>
          </li>
          <li>{t('modal.deleteUsers.description.bullet3')}</li>
        </StyledList>
      </section>
      <WarningBanner
        data-test={testIds.bannerWarning}
        title={t('modal.deleteUsers.warningBanner.title')}
      >
        <StyledList>
          <li>
            <Trans
              t={t}
              i18nKey="modal.deleteUsers.warningBanner.bullet1"
              components={{
                href: (
                  <StyledRouterLink
                    to={{ pathname: `/segmentation`, search: `?emailListId=${list.id}` }}
                  />
                ),
              }}
            >
              <Link href={`/segmentation?emailListId=${list.id}`} useReactRouter>
                Exporting this list
              </Link>{' '}
              to keep a record of deleted contacts
            </Trans>
          </li>
          <li>
            <Trans
              t={t}
              i18nKey="modal.deleteUsers.warningBanner.bullet2"
              components={{
                href: <StyledRouterLink to={{ pathname: `/campaigns` }} />,
              }}
            >
              <Link href={`/campaigns`}>Downloading historic campaign metrics</Link> associated with
              deleted contacts
            </Trans>
          </li>
        </StyledList>
      </WarningBanner>
      <StyledCheckbox
        id={testIds.chkboxConfirm}
        label={t('modal.deleteUsers.field.checkboxConfirm.label')}
        setChecked={(bool) => setCheckboxConfirm(!!bool)}
        status={checkboxConfirm}
      />
      {checkboxConfirm && (
        <StyledBodySection>
          <StyledFlexSection>
            <Trans
              t={t}
              i18nKey="modal.deleteUsers.duration"
              components={{
                strong: <strong />,
              }}
            >
              Deletions will typically be <strong>completed within 24 hours</strong> of request.
            </Trans>{' '}
            <Popover open={isHelpOpen} onOpenChange={setIsHelpOpen}>
              <StyledPopoverContent
                TitleContent={t('modal.deleteUsers.durationHelp.title')}
                side={PopoverPlacements.Top}
              >
                {t('modal.deleteUsers.durationHelp.message')}
              </StyledPopoverContent>
              <StyledPopoverTrigger>
                <Help24 />
              </StyledPopoverTrigger>
            </Popover>
          </StyledFlexSection>
          <StyledInputWrapper>
            <StyledInputLabel>
              {t('modal.deleteUsers.field.confirm.label', { confirmationString: deleteString })}
            </StyledInputLabel>
            <InputText
              data-test={testIds.inputConfirm}
              onChange={setInputConfirm}
              placeholder={t('modal.deleteUsers.confirmationString')}
              value={inputConfirm}
              width={'300px'}
            />
          </StyledInputWrapper>
        </StyledBodySection>
      )}
    </>
  );

  return (
    <Modal id={testIds.modal} isModalOpen onClose={closeModal} variant={MODAL_VARIANTS.DELETE}>
      <ModalHeader>{t('modal.deleteUsers.header')}</ModalHeader>
      <StyledModalBodyPlusHalf>
        <StyledDescList>
          <dt>{t('modal.deleteUsers.details.list.term')}</dt>
          <dd>
            {t('modal.deleteUsers.details.list.desc', { listName: list.name, listId: list.id })}
          </dd>
          <dt>{t('modal.deleteUsers.details.users.term')}</dt>
          <dd>
            {isListSizeLoading ? (
              <Skeleton width={100} />
            ) : (
              t('modal.deleteUsers.details.users.desc', {
                count: listSize,
                formattedCount: localizeNumber(listSize),
              })
            )}
          </dd>
        </StyledDescList>
        {!isListSizeLoading && deleteUsersModalBody}
      </StyledModalBodyPlusHalf>
      <ModalFooter>
        <Button
          data-test={testIds.buttonSchedule}
          isDisabled={!canSubmit}
          onClick={scheduleDeletion}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.DESTRUCTIVE}
        >
          {t('modal.deleteUsers.button.schedule')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
